import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { UserPlus, Calendar, MapPin, DollarSign, FileText, Trophy } from "lucide-react";
import {
	CURRENCY_OPTIONS,
	LOCATION_TYPES,
	CONTRACT_TYPES,
	LocationType,
	ContractType,
} from "@/types/workforce";
import { createWorkforce } from "@/hooks/workforce/useWorkforceActions";
import { CandidateSelector } from "@/components/candidates/CandidateSelector";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface SelectedCandidate {
	id: string;
	name: string;
}

interface JobClosedWonWorkforceModalProps {
	isOpen: boolean;
	onClose: () => void;
	jobId: string;
	jobTitle: string;
	onComplete?: () => void;
}

export const JobClosedWonWorkforceModal = ({
	isOpen,
	onClose,
	jobId,
	jobTitle,
	onComplete,
}: JobClosedWonWorkforceModalProps) => {
	const { t } = useTranslation();
	const [step, setStep] = useState<'ask' | 'form' | 'status'>('ask');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [organizationUuid, setOrganizationUuid] = useState<string | null>(null);

	// Candidate selection
	const [selectedCandidate, setSelectedCandidate] = useState<SelectedCandidate | null>(null);

	// Form state
	const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
	const [expectedEndDate, setExpectedEndDate] = useState("");
	const [location, setLocation] = useState("");
	const [locationType, setLocationType] = useState<LocationType | "">("");
	const [contractType, setContractType] = useState<ContractType | "">("");
	const [currency, setCurrency] = useState("EUR");
	const [purchasePriceDaily, setPurchasePriceDaily] = useState("");
	const [sellingPriceDaily, setSellingPriceDaily] = useState("");
	const [notes, setNotes] = useState("");

	// Status change options
	const [setCandidateHired, setSetCandidateHired] = useState(true);
	const [closeOtherMatches, setCloseOtherMatches] = useState(true);

	// Fetch organization UUID
	useEffect(() => {
		const fetchOrgUuid = async () => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) return;

			const { data: userOrg } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();

			if (userOrg?.organization_uuid)
				setOrganizationUuid(userOrg.organization_uuid);
		};

		fetchOrgUuid();
	}, []);

	// Reset form when modal opens
	useEffect(() => {
		if (isOpen) {
			setStep('ask');
			setSelectedCandidate(null);
			setStartDate(new Date().toISOString().split("T")[0]);
			setExpectedEndDate("");
			setLocation("");
			setLocationType("");
			setContractType("");
			setCurrency("EUR");
			setPurchasePriceDaily("");
			setSellingPriceDaily("");
			setNotes("");
			setSetCandidateHired(true);
			setCloseOtherMatches(true);
		}
	}, [isOpen]);

	const handleCandidateSelect = (candidate: { id: string; name: string }) => {
		setSelectedCandidate(candidate);
	};

	const handleCreateWorkforce = async () => {
		if (!organizationUuid || !selectedCandidate) return;

		setIsSubmitting(true);

		try {
			// Create workforce entry linked to the job and candidate
			const workforceId = await createWorkforce(
				{
					candidate_id: selectedCandidate.id,
					job_id: jobId,
					start_date: startDate,
					expected_end_date: expectedEndDate || undefined,
					status: "active",
					location: location || undefined,
					location_type: locationType || undefined,
					contract_type: contractType || undefined,
					currency,
					purchase_price_daily: purchasePriceDaily || undefined,
					selling_price_daily: sellingPriceDaily || undefined,
					notes: notes || undefined,
				},
				organizationUuid
			);

			if (!workforceId) return;

			// Mark the existing match as hired (if one exists)
			await supabase
				.from('matches')
				.update({ status: 'hired' })
				.eq('job_id', jobId)
				.eq('candidate_id', selectedCandidate.id)
				.not('status', 'in', '(hired,candidate_not_available,application_closed,not_selected,withdrawn,rejected)');

			// Move to status step
			setStep('status');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleFinalize = async () => {
		if (!selectedCandidate) return;

		setIsSubmitting(true);

		try {
			// Set candidate status to "Hired" if checked
			if (setCandidateHired)
				await supabase
					.from('candidates')
					.update({ status: 'hired', unavailable_until: null })
					.eq('id', selectedCandidate.id);

			// Close all other matches for this candidate if checked
			if (closeOtherMatches)
				await supabase.rpc('set_match_candidate_not_available', { p_candidate_id: selectedCandidate.id });

			onComplete?.();
			onClose();
		} catch {
			toast({
				title: t("common.error", "Error"),
				description: t("jobs.closedWon.finalizeError", "Failed to update candidate status"),
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
				{step === 'ask' && (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<Trophy className="h-5 w-5 text-emerald-600" />
								{t("jobs.closedWon.title", "Job Closed / Won")}
							</DialogTitle>
							<DialogDescription>
								{t("jobs.closedWon.description", "\"{{title}}\" has been marked as Closed/Won. Would you like to create a workforce assignment?", { title: jobTitle })}
							</DialogDescription>
						</DialogHeader>
						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={onClose}>
								{t("jobs.closedWon.skip", "No, skip")}
							</Button>
							<Button onClick={() => setStep('form')}>
								{t("jobs.closedWon.create", "Yes, create assignment")}
							</Button>
						</DialogFooter>
					</>
				)}

				{step === 'form' && (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<UserPlus className="h-5 w-5 text-emerald-600" />
								{t("jobs.closedWon.formTitle", "New Assignment")}
							</DialogTitle>
							<DialogDescription>
								{t("jobs.closedWon.formDescription", "Select a candidate and fill in the assignment details.")}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-2">
							{/* Candidate selector */}
							<div className="space-y-2">
								<Label className="text-sm font-medium">
									{t("jobs.closedWon.selectCandidate", "Candidate")}
									<span className="text-red-500 ml-1">*</span>
								</Label>
								{selectedCandidate ? (
									<div className="flex items-center justify-between p-3 rounded-sm border bg-muted/30">
										<div className="flex items-center gap-2">
											<div className="h-8 w-8 rounded-sm bg-blue-500 text-white flex items-center justify-center text-sm font-semibold">
												{selectedCandidate.name.charAt(0).toUpperCase()}
											</div>
											<span className="font-medium">{selectedCandidate.name}</span>
										</div>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setSelectedCandidate(null)}
										>
											{t("common.change", "Change")}
										</Button>
									</div>
								) : (
									<CandidateSelector
										onSelectCandidate={handleCandidateSelect}
										jobId={jobId}
									/>
								)}
							</div>

							{/* Only show rest of form after candidate is selected */}
							{selectedCandidate && (
								<>
									{/* Start Date */}
									<div className="space-y-2">
										<Label className="text-sm font-medium flex items-center gap-1.5">
											<Calendar className="h-3.5 w-3.5" />
											{t("workforce.fields.startDate", "Start Date")}
											<span className="text-red-500">*</span>
										</Label>
										<Input
											type="date"
											value={startDate}
											onChange={(e) => setStartDate(e.target.value)}
											className="h-10"
										/>
									</div>

									{/* Expected End Date */}
									<div className="space-y-2">
										<Label className="text-sm font-medium flex items-center gap-1.5">
											<Calendar className="h-3.5 w-3.5" />
											{t("workforce.fields.expectedEndDate", "Expected End Date")}
											<span className="text-muted-foreground text-xs ml-1">({t("common.optional", "optional")})</span>
										</Label>
										<Input
											type="date"
											value={expectedEndDate}
											onChange={(e) => setExpectedEndDate(e.target.value)}
											className="h-10"
										/>
									</div>

									{/* Location */}
									<div className="space-y-2">
										<Label className="text-sm font-medium flex items-center gap-1.5">
											<MapPin className="h-3.5 w-3.5" />
											{t("workforce.fields.location", "Location")}
										</Label>
										<Input
											value={location}
											onChange={(e) => setLocation(e.target.value)}
											placeholder={t("workforce.fields.locationPlaceholder", "Enter location...")}
											className="h-10"
										/>
									</div>

									{/* Location Type & Contract Type */}
									<div className="grid grid-cols-2 gap-3">
										<div className="space-y-2">
											<Label className="text-sm font-medium">
												{t("workforce.fields.locationType", "Work Type")}
											</Label>
											<Select value={locationType} onValueChange={(v) => setLocationType(v as LocationType)}>
												<SelectTrigger className="h-10">
													<SelectValue placeholder={t("common.select", "Select...")} />
												</SelectTrigger>
												<SelectContent>
													{LOCATION_TYPES.map((lt) => (
														<SelectItem key={lt.value} value={lt.value}>
															{t(`workforce.locationTypes.${lt.value}`, lt.label)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium">
												{t("workforce.fields.contractType", "Contract Type")}
											</Label>
											<Select value={contractType} onValueChange={(v) => setContractType(v as ContractType)}>
												<SelectTrigger className="h-10">
													<SelectValue placeholder={t("common.select", "Select...")} />
												</SelectTrigger>
												<SelectContent>
													{CONTRACT_TYPES.map((ct) => (
														<SelectItem key={ct.value} value={ct.value}>
															{t(`workforce.contractTypes.${ct.value}`, ct.label)}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>

									{/* Financial */}
									<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
										<div className="space-y-2">
											<Label className="text-sm font-medium flex items-center gap-1.5">
												<DollarSign className="h-3.5 w-3.5" />
												{t("workforce.fields.purchasePriceDaily", "Purchase (daily)")}
											</Label>
											<Input
												type="number"
												min="0"
												step="0.01"
												value={purchasePriceDaily}
												onChange={(e) => setPurchasePriceDaily(e.target.value)}
												placeholder="0.00"
												className="h-10"
											/>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium flex items-center gap-1.5">
												<DollarSign className="h-3.5 w-3.5" />
												{t("workforce.fields.sellingPriceDaily", "Selling (daily)")}
											</Label>
											<Input
												type="number"
												min="0"
												step="0.01"
												value={sellingPriceDaily}
												onChange={(e) => setSellingPriceDaily(e.target.value)}
												placeholder="0.00"
												className="h-10"
											/>
										</div>
										<div className="space-y-2">
											<Label className="text-sm font-medium">
												{t("workforce.fields.currency", "Currency")}
											</Label>
											<Select value={currency} onValueChange={setCurrency}>
												<SelectTrigger className="h-10">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{CURRENCY_OPTIONS.map((c) => (
														<SelectItem key={c} value={c}>{c}</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									</div>

									{/* Notes */}
									<div className="space-y-2">
										<Label className="text-sm font-medium flex items-center gap-1.5">
											<FileText className="h-3.5 w-3.5" />
											{t("workforce.statusChange.note", "Note")}
											<span className="text-muted-foreground text-xs ml-1">({t("common.optional", "optional")})</span>
										</Label>
										<Textarea
											value={notes}
											onChange={(e) => setNotes(e.target.value)}
											placeholder={t("candidates.hiredWorkforce.notesPlaceholder", "Additional notes about this assignment...")}
											rows={2}
											className="resize-none"
										/>
									</div>
								</>
							)}
						</div>

						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={() => setStep('ask')}>
								{t("common.back", "Back")}
							</Button>
							<Button
								onClick={handleCreateWorkforce}
								disabled={isSubmitting || !selectedCandidate || !startDate}
							>
								{isSubmitting
									? t("common.creating", "Creating...")
									: t("jobs.closedWon.createAssignment", "Create Assignment")
								}
							</Button>
						</DialogFooter>
					</>
				)}

				{step === 'status' && selectedCandidate && (
					<>
						<DialogHeader>
							<DialogTitle className="flex items-center gap-2">
								<Trophy className="h-5 w-5 text-emerald-600" />
								{t("jobs.closedWon.statusTitle", "Update Candidate Status")}
							</DialogTitle>
							<DialogDescription>
								{t("jobs.closedWon.statusDescription", "The assignment has been created. Would you like to update the candidate's status?")}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-2">
							<div className="flex items-start space-x-3 p-3 rounded-sm border">
								<Checkbox
									id="set-hired"
									checked={setCandidateHired}
									onCheckedChange={(checked) => setSetCandidateHired(checked === true)}
								/>
								<div>
									<Label htmlFor="set-hired" className="text-sm font-medium cursor-pointer">
										{t("jobs.closedWon.setCandidateHired", "Set {{name}}'s status to \"Hired\"", { name: selectedCandidate.name })}
									</Label>
									<p className="text-xs text-muted-foreground mt-1">
										{t("jobs.closedWon.setCandidateHiredHint", "Mark this candidate as hired in the system")}
									</p>
								</div>
							</div>

							<div className="flex items-start space-x-3 p-3 rounded-sm border">
								<Checkbox
									id="close-matches"
									checked={closeOtherMatches}
									onCheckedChange={(checked) => setCloseOtherMatches(checked === true)}
								/>
								<div>
									<Label htmlFor="close-matches" className="text-sm font-medium cursor-pointer">
										{t("jobs.closedWon.closeOtherMatches", "Close all other matches for this candidate")}
									</Label>
									<p className="text-xs text-muted-foreground mt-1">
										{t("jobs.closedWon.closeOtherMatchesHint", "Set all open matches (before offer stage) to \"Candidate Not Available\"")}
									</p>
								</div>
							</div>
						</div>

						<DialogFooter className="gap-2 sm:gap-0">
							<Button variant="outline" onClick={() => { onComplete?.(); onClose(); }}>
								{t("jobs.closedWon.skipStatus", "Skip")}
							</Button>
							<Button
								onClick={handleFinalize}
								disabled={isSubmitting || (!setCandidateHired && !closeOtherMatches)}
							>
								{isSubmitting
									? t("common.saving", "Saving...")
									: t("common.confirm", "Confirm")
								}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
